import React, { useCallback, useState } from 'react';
import { Stack, Box, IconButton, Tooltip, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { Company } from '../../../interfaces/users.interface';
import { DataCell, DataRow, DataRowActions } from '../../Tables/DataList';
import { tightTableSx } from '../../Tables/dataTableStyles';
import { getCompanySubsidiaries } from '../../../services/companyDetail';
import { useCompanyResource } from '../../Vendors/useCompanyResource';
import { TabEmpty, TabSkeletonRows, DEFAULT_PER_PAGE } from '../../Vendors/companyTabUi';
import { useSectionPager } from '../../Vendors/sectionPager';

type CompanySubsidiariesTabProps = {
  company: Company;
  companyCallback: any;
};

export default function CompanySubsidiariesTab(props: CompanySubsidiariesTabProps) {
  const { company } = props;
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PER_PAGE);

  const { items: subsidiaries, count, loading } = useCompanyResource<Company>(
    getCompanySubsidiaries,
    company.id,
    page,
    pageSize,
  );
  useSectionPager({ count, page, pageSize, setPage, setPageSize });

  const handleNavigateToSubsidiary = useCallback(
    (subsidiaryId: number) => {
      navigate(`/registry/${subsidiaryId}`);
    },
    [navigate],
  );

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Box sx={tightTableSx}>
        <DataRow header>
          <DataCell xs={10}>Subsidiary Name</DataCell>
          <DataCell xs={2}>&nbsp;</DataCell>
        </DataRow>

        {loading && <TabSkeletonRows rows={3} columns={[10, 2]} />}

        {!loading && subsidiaries.length === 0 && <TabEmpty label='No subsidiary companies' />}

        {!loading && subsidiaries.map((subsidiary) => (
          <DataRow key={subsidiary.id} onClick={() => {}} hover>
            <DataCell xs={10}>{subsidiary.name}</DataCell>
            <DataCell xs={2}>
              <DataRowActions>
                <Tooltip title="View Subsidiary">
                  <IconButton size='small' onClick={() => handleNavigateToSubsidiary(subsidiary.id!)}>
                    <OpenInNewIcon />
                  </IconButton>
                </Tooltip>
              </DataRowActions>
            </DataCell>
          </DataRow>
        ))}
      </Box>


      {company.parentCompany && (
        <Box sx={{ mt: 3, ...tightTableSx }}>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Parent Company
          </Typography>
          <DataRow header>
            <DataCell xs={10}>Parent Company Name</DataCell>
            <DataCell xs={2}>&nbsp;</DataCell>
          </DataRow>
          <DataRow onClick={() => {}} hover>
            <DataCell xs={10}>{company.parentCompany.name}</DataCell>
            <DataCell xs={2}>
              <DataRowActions>
                <Tooltip title="View Parent Company">
                  <IconButton size='small' onClick={() => handleNavigateToSubsidiary(company.parentCompany!.id)}>
                    <OpenInNewIcon />
                  </IconButton>
                </Tooltip>
              </DataRowActions>
            </DataCell>
          </DataRow>
        </Box>
      )}
    </Stack>
  );
}
